import { useState, useCallback } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDropzone } from "react-dropzone";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface PropertyPortalUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  item: {
    id: string;
    label: string;
    doc_type: string;
  };
  onSuccess: () => void;
}

const PropertyPortalUploadDialog = ({
  open,
  onOpenChange,
  token,
  item,
  onSuccess,
}: PropertyPortalUploadDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".webp"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleSubmit = async () => {
    if (!file) return;

    try {
      setUploading(true);

      // Upload file to storage
      const fileExt = file.name.split(".").pop();
      const fileName = `portal/immo/${token}/${Date.now()}_${item.doc_type}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Call the portal upload function
      const { data: success, error: rpcError } = await supabase.rpc(
        "portal_upload_property_document",
        {
          p_token: token,
          p_item_id: item.id,
          p_file_path: fileName,
          p_file_name: file.name,
        }
      );

      if (rpcError) throw rpcError;

      if (!success) {
        throw new Error("Échec de l'enregistrement du document");
      }

      toast({
        title: "Document déposé avec succès",
        description: "Votre document a été enregistré.",
      });

      onOpenChange(false);
      setFile(null);
      onSuccess();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Erreur lors du dépôt",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      onOpenChange(false);
      setFile(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Déposer : {item.label}</DialogTitle>
          <DialogDescription>
            Téléversez votre document au format PDF ou image (max 10 Mo)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
              isDragActive && "border-primary bg-primary/5",
              file && "border-emerald-500 bg-emerald-500/5"
            )}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-sm truncate max-w-[200px]">
                  {file.name}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {isDragActive
                    ? "Déposez le fichier ici"
                    : "Glissez-déposez ou cliquez pour sélectionner"}
                </p>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={!file || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Déposer
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyPortalUploadDialog;
